import DeleteIcon from '@mui/icons-material/Delete'
import DiscountIcon from '@mui/icons-material/Discount'
import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { message, Table } from 'antd'
import { size, sumBy } from 'lodash'
import { useEffect, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import dayjs from 'dayjs'
import CircularLoading from '../../../../../components/CircularProgress'
import { PropertyList } from '../../../../../components/PropertyList'
import { PropertyListItem } from '../../../../../components/PropertyListItem'
import { Tag } from '../../../../../components/Tag'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import { ONLY_DATE_TIME_FORMAT } from '../../../../../constants/global'
import { axiosInstance } from '../../../../../hooks/useAxios'
import {
  EOrderStatus,
  EOrderType,
  IOrder,
  IOrderItem,
} from '../../../../../types/order'
import { IProduct } from '../../../../../types/product'
import { validateMinMaxValue } from '../../../../../utils/validateMinMaxValue'
import {
  OrderFormAddProductModal,
  OrderFormAddProductModalForm,
} from './OrderFormAddProductModal'
import { numberFormat } from '../../../../../utils'

interface FilterGroupProps {
  isLoading: boolean
  data?: IOrderItem[]
  order: IOrder
  onSuccess?: () => void
  summary?: IOrder
}

const OrderProductsListDetailCard = (props: FilterGroupProps) => {
  const { isLoading, order, data, onSuccess, summary } = props
  const [isModalAddProduct, setIsModalAddProduct] = useState<boolean>(false)
  const [products, setProducts] = useState<IOrderItem[]>([])

  const editable =
    [EOrderStatus.PENDING, EOrderStatus.TELESALE, EOrderStatus.AI].includes(
      order?.status as EOrderStatus,
    ) && order?.trackingCode === null

  useEffect(() => {
    setProducts(data || [])
  }, [data])

  const onChangeDiscount = (key: string, value: any) => {
    setProducts(
      products.map((product) => {
        if (product.objectId === key) {
          return {
            ...product,
            discount: value,
            total: product.price * product.quantity - value,
          }
        }
        return product
      }),
    )
  }

  const handleUpdateDiscount = async (value: IOrderItem) => {
    const subTotal = (summary?.subTotal ?? 0) - value.discount
    if (subTotal < 1 && order.paymentMethod === EOrderType.COD) {
      message.error(
        'ไม่สามารถอัพเดทส่วนลดได้ เนื่องจากราคารวมเป็น 0 และมีรูปแบบการชำระเงิน เก็บเงินปลายทาง',
      )
      return
    }
    try {
      await axiosInstance.put(`/order/${order.objectId}/discount`, {
        product: value.objectId,
        discount: value.discount,
      })
      setProducts(order.items || [])
      onSuccess && onSuccess()
      message.success('อัพเดทส่วนลดสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const handleAddProduct = async (
    value: OrderFormAddProductModalForm & { product: IProduct },
  ) => {
    try {
      await axiosInstance.put(`/order/${order.objectId}/item/add`, {
        productCode: value.product.code,
        product: value.product.objectId,
        productName: value.product.name,
        categoryName: value.product.categoryName,
        category: value.product.category,
        subTotal: value.product.price * value.quantity,
        price: value.product.price,
        commission: value.product.commission,
        cost: value.product.cost,
        total: value.product.price * value.quantity - value.discount,
        free: value.isFree === 'yes',
        isCollection: value.product.isCollection,
        collections: value.product.collections || {},
        isTelesale: order.status === EOrderStatus.TELESALE,
        quantity: value.quantity,
        discount: value.discount,
        commissionUsername: value.commissionUsername || '',
        date: value.date,
        size: value.size ?? '',
      })
      onSuccess && onSuccess()
      message.success('เพิ่มสินค้าสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const onHandleDeleteProduct = async (objectId: string) => {
    if (order.items.length === 1) {
      message.error('ไม่สามารถลบสินค้าได้ เนื่องจากสินค้ามีอย่างน้อย 1 รายการ')
      return
    }
    try {
      await axiosInstance.put(`/order/${order.objectId}/item/delete`, {
        list: [objectId],
      })
      onSuccess && onSuccess()
      message.success('ลบสินค้าสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const columns = [
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'productName',
      key: 'productName',
      with: 300,
      render: (productName: string, product: IOrderItem) => {
        return (
          <>
            <Typography variant="subtitle1" color="text.primary">
              {productName}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              รหัสสินค้า : {product.productCode}
            </Typography>
            {product.meta?.size && (
              <Typography
                variant="subtitle2"
                fontSize={11}
                color="text.secondary"
              >
                ขนาด : {product.meta?.size ?? '-'}
              </Typography>
            )}
            <Box
              mt={0.5}
              display={'flex'}
              alignItems={'center'}
              gap={1}
              flexWrap={'wrap'}
            >
              {product.free && <Tag color={`success`}>ฟรี</Tag>}
              {product.isCollection && <Tag color={`info`}>เซ็ต</Tag>}
            </Box>
          </>
        )
      },
    },
    {
      title: 'เพิ่มโดย',
      dataIndex: 'actor',
      key: 'actor',
      align: 'center' as const,
      render: (actor: string, product: IOrderItem) => (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            {actor}
          </Typography>
          <Typography variant="subtitle2" fontSize={11} color="text.secondary">
            {dayjs(product.orderDate).format(ONLY_DATE_TIME_FORMAT)}
          </Typography>
          <Box
            mt={0.5}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            flexWrap={'wrap'}
          >
            {product.orderDateType === 'order' ? (
              <Tag color={`warning`}>วันที่คำสั่งซื้อ</Tag>
            ) : (
              <Tag color={`primary`}>วันที่ปัจจุบัน</Tag>
            )}
          </Box>
        </>
      ),
    },
    {
      title: 'รับค่าคอม',
      dataIndex: 'commissionUsername',
      key: 'commissionUsername',
      align: 'center' as const,
      render: (commissionUsername: string, item: IOrderItem) => {
        return (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              {commissionUsername ?? '-'}
            </Typography>
            {item.commissionUserFullName && (
              <Typography
                variant="subtitle2"
                fontSize={12}
                color="text.secondary"
              >
                ({item.commissionUserFullName ?? '-'})
              </Typography>
            )}
          </>
        )
      },
    },
    {
      title: 'ราคาต่อชิ้น',
      dataIndex: 'price',
      key: 'price',
      align: 'right' as const,
      render: (price: number) => (
        <Typography variant="subtitle2" color="text.secondary">
          {numberFormat(price)}
        </Typography>
      ),
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      align: 'right' as const,
      key: 'quantity',
      render: (quantity: number) => (
        <Typography variant="subtitle2" color="text.secondary">
          {quantity}
        </Typography>
      ),
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'key',
      key: 'key',
      align: 'right' as const,
      with: 150,
      render: (key: string, item: IOrderItem) => (
        <TextField
          sx={{
            minWidth: 100,
            maxWidth: 150,
            textAlign: 'right',
          }}
          disabled={!editable || item.free}
          fullWidth
          label="ส่วนลด"
          value={
            products.find((product) => product.objectId === key)?.discount || ''
          }
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onChange={(e) => {
            const amount = validateMinMaxValue(
              e.target.value,
              0,
              (data?.find((product) => product.objectId === key)?.price || 0) *
                (data?.find((product) => product.objectId === key)?.quantity ||
                  0),
            )

            onChangeDiscount(key!, isNaN(Number(amount)) ? 0 : amount)
          }}
        />
      ),
    },
    {
      title: 'ราคารวม',
      dataIndex: 'total',
      align: 'right' as const,
      key: 'total',
      render: (total: number) => (
        <Typography variant="subtitle2" color="text.secondary">
          {numberFormat(total)}
        </Typography>
      ),
    },
    {
      dataIndex: '',
      key: 'action',
      render: (_: undefined, product: IOrderItem) => {
        return (
          <Grid container spacing={2} justifyContent="flex-end">
            {product.discount !==
              data?.find(
                (productData) => productData.objectId === product.objectId,
              )?.discount &&
              editable && (
                <Grid item>
                  <Button
                    startIcon={<DiscountIcon />}
                    color="info"
                    size="medium"
                    variant={'contained'}
                    onClick={() => handleUpdateDiscount(product)}
                  >
                    บันทึกส่วนลด
                  </Button>
                </Grid>
              )}
            <Grid item>
              {editable && (
                <IconButton
                  onClick={() => onHandleDeleteProduct(product.objectId!)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        )
      },
    },
  ]

  const expandedRowRender = (record: IOrderItem & { key: number }) => {
    const items = record.collections || []
    const columns: any = [
      {
        title: 'สินค้า',
        dataIndex: 'productName',
        align: 'center' as const,
        key: 'productName',
        render: (productName: any, data: IOrderItem) => {
          return <>{productName}</>
        },
      },
      {
        title: 'รหัสสินค้า',
        dataIndex: 'productCode',
        align: 'center' as const,
        key: 'productCode',
        render: (productCode: any, data: IOrderItem) => {
          return <>{productCode}</>
        },
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        align: 'center' as const,
        key: 'quantity',
        render: (quantity: any, data: IOrderItem) => {
          return <>{quantity}</>
        },
      },
    ]
    return (
      <Table
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre: any, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={items}
        pagination={false}
      />
    )
  }

  //sumby summary items collections quantity

  const orderHasCollectionItems = products.some(
    (product) => product.isCollection,
  )
  const productSetCount = orderHasCollectionItems
    ? sumBy(summary?.items, (item) =>
        item.collections ? sumBy(item.collections, 'quantity') : 0,
      )
    : 0
  const productNoneSetCount = sumBy(summary?.items, (item) => {
    return item.isCollection ? 0 : item.quantity
  })

  // let quantity = summary?.quantity || 0
  // if (orderHasCollectionItems) {
  //   quantity = productSetCount + productNoneSetCount
  // }

  let summaryText = summary
    ? `${summary.items.length} รายการ ${summary?.quantity} ชิ้น`
    : ''

  if (orderHasCollectionItems) {
    const summaryAdditionText = []
    if (productSetCount) {
      summaryAdditionText.push(`สินค้าแบบเซ็ต ${productSetCount} ชิ้น`)
    }
    if (productNoneSetCount) {
      summaryAdditionText.push(`สินค้าทั่วไป ${productNoneSetCount} ชิ้น`)
    }
    summaryText = `${summaryText} (${summaryAdditionText.join(', ')})`
  }

  return (
    <>
      <OrderFormAddProductModal
        onAdd={handleAddProduct}
        isOpen={isModalAddProduct}
        onClose={(visible) => setIsModalAddProduct(visible)}
      />
      <CircularLoading isLoading={isLoading} />
      <Card>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
          }}
        >
          <CardHeader title="รายการสินค้า" />
          {editable && (
            <Button
              startIcon={<AddIcon />}
              color="warning"
              size="medium"
              variant="text"
              onClick={() => setIsModalAddProduct(!isModalAddProduct)}
            >
              เพิ่มสินค้า
            </Button>
          )}
        </Box>
        <Divider />
        <Box
          sx={{
            overflowX: 'auto',
          }}
        >
          <Table
            size={'middle'}
            columns={columns}
            expandable={{
              expandedRowRender: expandedRowRender as any,
              rowExpandable: (record) => record?.isCollection ?? false,
            }}
            dataSource={products?.map((product: IOrderItem) => {
              return {
                ...product,
                key: product.objectId,
              }
            })}
            bordered
            pagination={{
              showSizeChanger: false,
              hideOnSinglePage: true,
            }}
          />
        </Box>
        <Divider />
        <PropertyList>
          <PropertyListItem
            align={`horizontal`}
            label="สรุปรายการสินค้า"
            value={summaryText}
          />
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="รวม"
            value={`${numberFormat(summary?.subTotal ?? 0)} บาท`}
          />
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="ส่วนลด"
            value={`${numberFormat(
              products.reduce((acc, product) => acc + product.discount, 0),
            )} บาท`}
          />
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="ราคาสินค้ารวม"
            value={`${numberFormat(summary?.total ?? 0)} บาท`}
          />
          <Divider />
        </PropertyList>
      </Card>
    </>
  )
}

export default OrderProductsListDetailCard
